/** Production endpoint provisioning. Relay data never supplies roots or daemon identity pins. */
import {
  type AgreementWrap,
  type ApprovalBatchItem,
  accountKeyCommitment,
  approvalBatchItem,
  createAccountDataKey,
  createDirectoryChallenge,
  createRevocationChallenge,
  type DeviceEndorsement,
  type EcPublicJwk,
  isSelfJoin,
  isSessionEndorsement,
  jwkThumbprint,
  KEY_DIRECTORY_DOMAIN,
  type KeyDirectory,
  KeyDirectoryVerifier,
  type KeyRecipient,
  nextRevocationList,
  type SignedCommand,
  type SignedKeyDirectory,
  type SignedRevocationList,
  signKeyDirectory,
  signP256,
  signSelfJoin,
  signStatement,
  type TrustStore,
  verifyDeviceEndorsement,
} from '@viko/crypto'
import {
  type ContentDirectoryCatchUpPage,
  EncryptedContent,
  SESSION_DEVICE_DOMAIN,
} from '@viko/protocol'
import { type Api, ApiError } from './api.js'
import { hasApprovedContentDaemon, verifiedDaemonContentKey } from './content-enrollment.js'
import { keysApi } from './content-keys-api.js'
import { loadContentTrust, saveContentTrust } from './content-trust.js'
import { ContentVault } from './content-vault.js'
import { loadDevice, type StoredDevice } from './device-key.js'
import { rememberAccount } from './last-account.js'
import type { PasskeyProvider } from './passkeys.js'
import { genesisEscrows } from './recovery-kit.js'
import type { CommandOutcome } from './types.js'

/** What recovery/escrow flows may use inside the session's serialized ceremony queue. */
export interface KeyContext {
  accountId: string
  api: Api
  passkeys: PasskeyProvider
  vault: ContentVault
  local(): Promise<{ trust: TrustStore; device: StoredDevice }>
  refresh(
    trust: TrustStore,
    device: StoredDevice,
    extraKeys?: (epoch: number) => Promise<Uint8Array | undefined>,
  ): Promise<KeyDirectoryVerifier>
  publishNext(
    trust: TrustStore,
    device: StoredDevice,
    input: {
      raw: Uint8Array
      recipients: KeyRecipient[]
      epoch?: number
      escrowKeys?: { recipientId: string; publicKey: EcPublicJwk }[]
      approval?: 'passkey' | 'device'
    },
  ): Promise<{ statement: KeyDirectory; unwrappedEscrows: string[] }>
  escrowKeys(): ContentDirectoryCatchUpPage['escrowKeys']
  /** Trusted escrow reader → device recipient, signed by its own device key (no ceremony). */
  joinTrusted(trust: TrustStore, device: StoredDevice): Promise<boolean>
}

export type MachineAccess =
  | 'granted'
  | 'needs-grant'
  | 'identity-pending'
  | 'approve-elsewhere'
  | 'locked'

/** A zero-tap grant was requested, but only a passkey ceremony can make this change. */
export class GrantNeedsPasskey extends Error {
  constructor() {
    super('This change needs your passkey')
  }
}

/**
 * A genesis directory prepared for a shared passkey tap (the enrollment approval): `item` goes in
 * the approval batch; `publish` sends it once the batched assertion exists. Holds the fresh ADK
 * until published or discarded.
 */
export interface PreparedGenesis {
  item: ApprovalBatchItem
  publish(assertion: SignedKeyDirectory['approval'] & { type: 'passkey' }): Promise<void>
  discard(): void
}

/**
 * A "Just this session" browser is never a directory recipient: a wrap to its key would outlive
 * the session (docs/security.md "Session devices"). It reads as an escrow reader instead.
 */
export function refuseSessionRecipient(
  device: Pick<StoredDevice, 'deviceKeyId' | 'endorsement'>,
  recipients: readonly KeyRecipient[],
) {
  if (
    isSessionEndorsement(device.endorsement?.statement) &&
    recipients.some((r) => r.type === 'device' && r.id === device.deviceKeyId)
  )
    throw new Error('Trust this device to add it to your encrypted account')
}

/** How long one successful session + directory check covers later reads (read-budget). */
export const READ_CHECK_MS = 30_000
/** Status surfaces (machine access badges, lock probe, fresh-account check) share it this long. */
export const STATUS_CHECK_MS = 2_000

/** This browser's local trust refused (not a network or relay failure). */
class LocalTrustError extends Error {
  constructor(readonly inner: unknown) {
    super(inner instanceof Error ? inner.message : String(inner))
  }
}
/** Callers outside the shared check see the original local-trust error. */
const unwrapLocal = (e: unknown): never => {
  throw e instanceof LocalTrustError ? e.inner : e
}

export class BrowserContentSession {
  readonly vault = new ContentVault()
  private work: Promise<unknown> = Promise.resolve()
  constructor(
    private readonly api: Api,
    private readonly passkeys: PasskeyProvider,
    private readonly now: () => number = Date.now,
  ) {}
  async accountId() {
    const s = await this.api.session()
    if (!s.authenticated || s.scope !== 'full' || !s.userId)
      throw new Error('Sign in to unlock encrypted content')
    void rememberAccount(s.userId) // unlock-via-mac: a returning sign-in carries its request
    return s.userId
  }
  private async local(accountId: string) {
    const trust = await loadContentTrust(accountId)
    const device = await loadDevice()
    if (!device?.endorsement || !(await trust.addEndorsement(device.endorsement)).ok)
      throw new Error('Trust this browser with a locally approved passkey')
    await this.vault.ensureIdentity(accountId)
    return { trust, device }
  }
  /** Escrow public keys from the latest catch-up page; wrapped to only after thumbprint checks. */
  private escrowKeys: ContentDirectoryCatchUpPage['escrowKeys'] = []
  private async refresh(
    accountId: string,
    trust: TrustStore,
    device: StoredDevice,
    extraKeys?: (epoch: number) => Promise<Uint8Array | undefined>,
  ) {
    const catchUp = typeof (this.api as Partial<Api>).contentKeys === 'function'
    for (let pageNo = 0; pageNo < 256; pageNo++) {
      let directory = await this.vault.directory(accountId, trust)
      const before = directory.checkpoint?.revision ?? 0
      const request = { afterRevision: before, recipientId: device.deviceKeyId }
      const page: ContentDirectoryCatchUpPage = catchUp
        ? await keysApi(this.api).catchUp(before, device.deviceKeyId)
        : {
            ...(await this.api.readContentDirectory(request)),
            epochWraps: [],
            revocations: null,
            escrowKeys: [],
          }
      if (page.head < before) throw new Error('Encrypted directory rollback')
      // Passkey-signed revocations are verified against local pins before any snapshot.
      if (page.revocations) {
        const applied = await trust.applyRevocationList(page.revocations as SignedRevocationList)
        if (applied.ok) await saveContentTrust(accountId, trust)
        else if (applied.reason !== 'stale') throw new Error('Encrypted revocation list rejected')
      }
      // Device-approved snapshots (zero-tap grants): learn their signers' passkey-signed
      // endorsements, verified against this browser's pinned passkeys (never taken on trust).
      for (const e of page.endorsements ?? []) await trust.addEndorsement(e as DeviceEndorsement)
      if (page.directories.length) {
        const last = page.directories.at(-1)!.statement.epoch
        const pending = [
          ...page.epochWraps.map((w) => ({ epoch: w.epoch, wrap: w.wrap as AgreementWrap })),
          ...(page.wrap ? [{ epoch: last, wrap: page.wrap as AgreementWrap }] : []),
        ]
        directory = await this.vault.acceptChain(accountId, trust, page.directories, Date.now(), {
          recipientId: device.deviceKeyId,
          pending,
          ...(extraKeys ? { extra: extraKeys } : {}),
        })
      }
      const after = directory.checkpoint?.revision ?? 0
      if (after > page.head || (after < page.head && after <= before))
        throw new Error('Encrypted directory gap')
      const isRecipient =
        directory.checkpoint?.statement.recipients.some(
          (r) => r.id === device.deviceKeyId && r.type === 'device',
        ) && !directory.checkpoint.unproven?.includes(device.deviceKeyId)
      if (isRecipient)
        for (const w of [...page.epochWraps].sort((a, b) => a.epoch - b.epoch))
          if (w.revision <= after)
            await this.vault.installEpoch(directory, device.deviceKeyId, w.epoch, w.wrap)
      if (after === page.head) {
        if (page.wrap && isRecipient) {
          if (page.wrapRevision !== after) throw new Error('Encrypted wrap revision mismatch')
          await this.vault.install(directory, device.deviceKeyId, page.wrap)
        }
        this.escrowKeys = page.escrowKeys
        return directory
      }
    }
    throw new Error('Encrypted directory history limit')
  }
  /** Serialize local ceremonies; CAS at the relay and in IDB also handles other browsers. */
  currentKey(machineId?: string, opts: { passkey?: boolean } = {}) {
    const next = this.work
      .then(() => this.provision(machineId, opts.passkey !== false))
      .finally(() => this.invalidate())
    this.work = next.catch(() => {})
    return next
  }
  private async provision(machineId?: string, allowPasskey = true) {
    const accountId = await this.accountId()
    const { trust, device } = await this.local(accountId)
    let directory = await this.refresh(accountId, trust, device)
    void this.bindSessionOnce(accountId, device)
    // A signed revocation is pending: rotate to an independent epoch before any new write.
    if (
      directory.checkpoint &&
      directory.checkpoint.statement.revocationSeq !== trust.revocationSeq
    ) {
      if (!allowPasskey) throw new GrantNeedsPasskey()
      await this.rotateLocked(accountId, trust, device)
      directory = await this.vault.directory(accountId, trust)
    }
    // A trusted browser still reading through its passkey's escrow joins now (zero taps), so the
    // next rotation elsewhere reaches it. Best effort: reading and sealing work either way.
    if (await this.joinTrusted(accountId, trust, device).catch(() => false))
      directory = await this.vault.directory(accountId, trust)
    const checkpoint = directory.checkpoint
    if (!machineId && checkpoint)
      return { accountId, ...(await this.vault.currentKey(accountId, trust, device.deviceKeyId)) }
    const existing = checkpoint?.statement.recipients.find((r) => r.id === machineId)
    // A daemon already listed in the directory this browser verified against its pinned passkeys
    // holds the current key: sealing to it grants nothing new, so a browser that never approved
    // the machine itself (no enrollment pin: another browser added it) can still send it
    // commands. Its own pin, when it has one, still cross-checks the listed key below.
    if (
      existing?.type === 'daemon' &&
      machineId &&
      !(await hasApprovedContentDaemon(accountId, machineId))
    ) {
      directory.recipient(machineId) // throws for a revoked or unproven recipient
      return { accountId, ...(await this.vault.currentKey(accountId, trust, device.deviceKeyId)) }
    }
    const publicKey = machineId
      ? await verifiedDaemonContentKey(
          accountId,
          machineId,
          await this.api.readContentIdentity(machineId),
        )
      : undefined
    if (existing && machineId && publicKey) {
      if (
        existing.type !== 'daemon' ||
        (await jwkThumbprint(existing.publicKey)) !== (await jwkThumbprint(publicKey))
      )
        throw new Error('Machine content identity changed; recovery approval required')
      directory.recipient(machineId)
      return { accountId, ...(await this.vault.currentKey(accountId, trust, device.deviceKeyId)) }
    }
    const identity = await this.vault.ensureIdentity(accountId)
    const recipients: KeyRecipient[] = [
      ...(checkpoint?.statement.recipients ?? [
        { type: 'device' as const, id: device.deviceKeyId, publicKey: identity.publicJwk },
      ]),
      ...(machineId && publicKey ? [{ type: 'daemon' as const, id: machineId, publicKey }] : []),
    ]
    if (checkpoint) {
      // Zero-tap grant (docs/e2e.md): appending a daemon whose content key verified against this
      // browser's passkey-approved enrollment pin is signed by this endorsed device recipient.
      const approval =
        machineId &&
        publicKey &&
        !trust.isDeviceRevoked(machineId) &&
        trust.lookupDevice(device.deviceKeyId).status === 'ok' &&
        checkpoint.statement.recipients.some(
          (r) => r.type === 'device' && r.id === device.deviceKeyId,
        )
          ? ('device' as const)
          : ('passkey' as const)
      if (approval === 'passkey' && !allowPasskey) throw new GrantNeedsPasskey()
      await this.vault.withCurrentKeyMaterial(accountId, trust, device.deviceKeyId, (raw) =>
        this.publishNext(accountId, trust, device, { raw, recipients, approval }),
      )
    } else {
      if (!allowPasskey) throw new GrantNeedsPasskey()
      const raw = createAccountDataKey()
      // Recovery kit: escrows prepared at sign-up join genesis (the genesis recovery wrap).
      const kit = await genesisEscrows(accountId)
      try {
        await this.publishNext(accountId, trust, device, {
          raw,
          recipients: [...recipients, ...kit.recipients],
          epoch: 1,
          escrowKeys: kit.escrowKeys,
        })
        await kit.publish(this.api, raw).catch(() => {})
      } finally {
        raw.fill(0)
      }
    }
    return { accountId, ...(await this.vault.currentKey(accountId, trust, device.deviceKeyId)) }
  }

  /**
   * Sign (passkey), verify locally, wrap to every ECDH/escrow recipient from the verified
   * candidate, publish with relay CAS, then pin and install this browser's own wrap.
   */
  private async publishNext(
    accountId: string,
    trust: TrustStore,
    device: StoredDevice,
    next: {
      raw: Uint8Array
      recipients: KeyRecipient[]
      epoch?: number
      escrowKeys?: { recipientId: string; publicKey: EcPublicJwk }[]
      approval?: 'passkey' | 'device'
    },
  ) {
    refuseSessionRecipient(device, next.recipients)
    const checkpoint = (await this.vault.state(accountId))?.checkpoint
    // A rotation leaves out self-joined recipients this browser could not prove (docs/e2e.md):
    // listing one in a new epoch would vouch for it.
    if (checkpoint?.unproven?.length && (next.epoch ?? 0) > checkpoint.statement.epoch)
      next = {
        ...next,
        recipients: next.recipients.filter((r) => !checkpoint.unproven!.includes(r.id)),
      }
    const statement = await this.nextStatement(accountId, trust, next)
    // After a passkey revocation (lost device), only surviving passkeys can sign.
    const live = trust.credentials().map((c) => c.credentialId)
    const anyRevoked = trust.toJSON().revokedCredentials.length > 0
    const selfJoin =
      next.approval === 'device' &&
      !!checkpoint &&
      isSelfJoin(checkpoint.statement, statement, device.deviceKeyId)
    const approval: SignedKeyDirectory['approval'] =
      next.approval === 'device'
        ? selfJoin
          ? (await signSelfJoin(statement, checkpoint!.digest, device, next.raw)).approval
          : (await signKeyDirectory(statement, device)).approval
        : {
            type: 'passkey',
            assertion: await this.passkeys.assert(await createDirectoryChallenge(statement), {
              rpId: trust.rp.rpId,
              ...(anyRevoked ? { allowCredentials: live } : {}),
            }),
          }
    return this.publishSigned(accountId, trust, device, next, { statement, approval })
  }
  private async nextStatement(
    accountId: string,
    trust: TrustStore,
    next: { raw: Uint8Array; recipients: KeyRecipient[]; epoch?: number },
  ): Promise<KeyDirectory> {
    const checkpoint = (await this.vault.state(accountId))?.checkpoint
    const epoch = next.epoch ?? checkpoint?.statement.epoch ?? 1
    return {
      version: 1,
      accountId,
      revision: (checkpoint?.revision ?? 0) + 1,
      previous: checkpoint?.digest ?? null,
      epoch,
      keyCommitment: await accountKeyCommitment(next.raw, accountId, epoch),
      revocationSeq: trust.revocationSeq,
      issuedAt: Date.now(),
      recipients: next.recipients,
    }
  }
  private async publishSigned(
    accountId: string,
    trust: TrustStore,
    device: StoredDevice,
    next: { raw: Uint8Array; escrowKeys?: { recipientId: string; publicKey: EcPublicJwk }[] },
    signed: SignedKeyDirectory,
  ) {
    const { statement } = signed
    const checkpoint = (await this.vault.state(accountId))?.checkpoint
    if ((checkpoint?.revision ?? 0) + 1 !== statement.revision)
      throw new Error('Encrypted directory changed; try again')
    // The key being published proves this browser's own self-join.
    const ownKey = async (epoch: number) =>
      epoch === statement.epoch ? next.raw.slice() : undefined
    const candidate = new KeyDirectoryVerifier(accountId, trust, checkpoint, { epochKey: ownKey })
    const accepted = await candidate.accept(signed)
    const escrowKeys = [...(next.escrowKeys ?? []), ...this.escrowKeys]
    const wraps = []
    const unwrappedEscrows: string[] = []
    for (const recipient of statement.recipients) {
      // Never wrap to an unproven self-joined recipient.
      if (accepted.unproven?.includes(recipient.id)) continue
      if (recipient.type === 'device' || recipient.type === 'daemon')
        wraps.push({
          recipientId: recipient.id,
          wrap: await candidate.wrapFor(next.raw, recipient.id),
        })
      else {
        const key = escrowKeys.find((k) => k.recipientId === recipient.id)
        if (!key) unwrappedEscrows.push(recipient.id)
        else
          wraps.push({
            recipientId: recipient.id,
            wrap: await candidate.wrapForEscrow(next.raw, recipient.id, key.publicKey),
          })
      }
    }
    const published = await this.api.publishContentDirectory({ directory: signed, wraps })
    if (published.revision !== accepted.revision || published.digest !== accepted.digest)
      throw new Error('Encrypted directory publication mismatch')
    const pinned = await this.vault.acceptDirectory(accountId, trust, signed, Date.now(), {
      recipientId: device.deviceKeyId,
      extra: ownKey,
    })
    const own = wraps.find((w) => w.recipientId === device.deviceKeyId)
    if (!own) throw new Error('This browser needs an approved key transfer')
    await this.vault.install(pinned, device.deviceKeyId, own.wrap)
    return { statement, unwrappedEscrows }
  }

  /**
   * Fresh account only: prepare the genesis directory (this browser as the first recipient) so
   * the enrollment approval's single passkey tap also authorizes it (docs/e2e.md). Returns
   * undefined when a directory exists or this browser is not trusted for encrypted content.
   */
  prepareGenesis(): Promise<PreparedGenesis | undefined> {
    const next = this.work.then(async (): Promise<PreparedGenesis | undefined> => {
      const accountId = await this.accountId()
      let local: { trust: TrustStore; device: StoredDevice }
      try {
        local = await this.local(accountId)
      } catch {
        return undefined
      }
      const { trust, device } = local
      // A "Just this session" browser never becomes the genesis recipient (it would outlive it).
      if (isSessionEndorsement(device.endorsement?.statement)) return undefined
      if ((await this.refresh(accountId, trust, device)).checkpoint) return undefined
      const identity = await this.vault.ensureIdentity(accountId)
      const raw = createAccountDataKey()
      const kit = await genesisEscrows(accountId)
      const statement = await this.nextStatement(accountId, trust, {
        raw,
        recipients: [
          { type: 'device', id: device.deviceKeyId, publicKey: identity.publicJwk },
          ...kit.recipients,
        ],
        epoch: 1,
      })
      let done = false
      const discard = () => {
        done = true
        raw.fill(0)
      }
      return {
        item: approvalBatchItem(KEY_DIRECTORY_DOMAIN, await createDirectoryChallenge(statement)),
        discard,
        publish: (approval) => {
          const run = this.work.then(async () => {
            if (done) throw new Error('Genesis already used')
            try {
              await this.publishSigned(
                accountId,
                trust,
                device,
                { raw, escrowKeys: kit.escrowKeys },
                { statement, approval },
              )
              // A failed upload keeps the kit pending; You › Security re-adds it.
              await kit.publish(this.api, raw).catch(() => {})
            } finally {
              discard()
            }
          })
          this.work = run.catch(() => {})
          return run
        },
      }
    })
    this.work = next.catch(() => {})
    return next
  }

  /**
   * Trusted browsers follow rotations (docs/e2e.md): a browser the user chose to *trust* that
   * reads through its passkey's PRF escrow appends itself to the directory as a device recipient,
   * signed by its own endorsed device key. Verifiers accept that only as an exact self-append (same
   * epoch, commitment and revocation state) by a live, non-session endorsement. Later rotations
   * then wrap to it and it installs the new epoch on its next sync, with no passkey tap. A
   * "Just this session" browser never joins; a revoked one is refused and left out of new epochs.
   * Returns true when it joined.
   */
  private async joinTrusted(accountId: string, trust: TrustStore, device: StoredDevice) {
    if (!device.endorsement || isSessionEndorsement(device.endorsement.statement)) return false
    const own = trust.lookupDevice(device.deviceKeyId)
    if (own.status !== 'ok' || own.device.session) return false
    const state = await this.vault.state(accountId)
    const head = state?.checkpoint
    if (
      !head ||
      head.statement.revocationSeq !== trust.revocationSeq ||
      head.statement.recipients.some((r) => r.id === device.deviceKeyId) ||
      !state.epochs.includes(head.statement.epoch)
    )
      return false
    const identity = await this.vault.ensureIdentity(accountId)
    await this.vault.withCurrentKeyMaterial(
      accountId,
      trust,
      device.deviceKeyId,
      (raw) =>
        this.publishNext(accountId, trust, device, {
          raw,
          recipients: [
            ...head.statement.recipients,
            { type: 'device', id: device.deviceKeyId, publicKey: identity.publicJwk },
          ],
          approval: 'device',
        }),
      { escrowReader: true },
    )
    return true
  }
  private joinQueued = false
  /** Queue one best-effort join behind the ceremony queue (read paths never wait for it). */
  private joinSoon(accountId: string) {
    if (this.joinQueued) return
    this.joinQueued = true
    const next = this.work.then(async () => {
      const { trust, device } = await this.local(accountId)
      await this.refresh(accountId, trust, device)
      await this.joinTrusted(accountId, trust, device)
    })
    this.work = next.catch(() => {})
  }
  /** A reader that is not yet a recipient and could join (cheap local check, no network). */
  private async couldJoin(accountId: string, device: StoredDevice) {
    if (!device.endorsement || isSessionEndorsement(device.endorsement.statement)) return false
    const head = (await this.vault.state(accountId))?.checkpoint
    return !!head && !head.statement.recipients.some((r) => r.id === device.deviceKeyId)
  }

  /**
   * Whether `id` is provably a "Just this session" browser, so removing it needs no new epoch:
   * its passkey-signed endorsement (relayed with the device row) must verify against this
   * browser's pinned passkeys with `scope: 'session'`, and it must not be a recipient of the
   * verified directory. Relay data alone never skips a rotation; anything else returns false.
   */
  async verifiedSessionDevice(id: string, endorsement: unknown): Promise<boolean> {
    try {
      const e = endorsement as DeviceEndorsement | undefined
      if (!e?.statement || e.statement.deviceKeyId !== id || !isSessionEndorsement(e.statement))
        return false
      const accountId = await this.accountId()
      const trust = await loadContentTrust(accountId)
      // Checked as of its own issue time: an expired session device is still provably one.
      const r = await verifyDeviceEndorsement(e, {
        credentials: trust.credentials(),
        rp: trust.rp,
        now: e.statement.issuedAt,
      })
      if (!r.ok || !r.device.session || r.device.deviceKeyId !== id) return false
      const head = (await this.vault.state(accountId))?.checkpoint
      return !head?.statement.recipients.some((x) => x.id === id)
    } catch {
      return false
    }
  }

  /** New epoch after a revocation: independent ADK, wrapped to surviving recipients only. */
  private async rotateLocked(accountId: string, trust: TrustStore, device: StoredDevice) {
    const checkpoint = (await this.vault.state(accountId))?.checkpoint
    if (!checkpoint) throw new Error('content locked')
    const survivors = checkpoint.statement.recipients.filter((r) =>
      r.type === 'device' || r.type === 'daemon'
        ? !trust.isDeviceRevoked(r.id) &&
          !(r.type === 'device' && trust.lookupDevice(r.id).status === 'revoked')
        : !(r.type === 'prf' && trust.isCredentialRevoked(r.id)),
    )
    if (!survivors.some((r) => r.id === device.deviceKeyId && r.type === 'device'))
      throw new Error('This browser was revoked; recover access to continue')
    const raw = createAccountDataKey()
    try {
      return await this.publishNext(accountId, trust, device, {
        raw,
        recipients: survivors,
        epoch: checkpoint.statement.epoch + 1,
      })
    } finally {
      raw.fill(0)
    }
  }

  /**
   * Revoke devices/machines (and optionally passkeys): passkey-signed cumulative list, relay
   * ends their sessions and sockets, then an immediate rotation so they cannot read new data.
   */
  revoke(add: { devices?: string[]; credentials?: string[] }, opts: { signWith?: string[] } = {}) {
    const next = this.work.then(async () => {
      const accountId = await this.accountId()
      const { trust, device } = await this.local(accountId)
      await this.refresh(accountId, trust, device)
      if (add.devices?.includes(device.deviceKeyId))
        throw new Error('Revoke this browser from another trusted device')
      const list = nextRevocationList(trust, add)
      const signed = {
        list,
        // A revoked passkey can't sign its own revocation: `signWith` limits the prompt to others.
        assertion: await this.passkeys.assert(await createRevocationChallenge(list), {
          rpId: trust.rp.rpId,
          ...(opts.signWith?.length ? { allowCredentials: opts.signWith } : {}),
        }),
      }
      const relayed = await keysApi(this.api).revoke(signed)
      const applied = await trust.applyRevocationList(signed)
      if (!applied.ok) throw new Error('Revocation list rejected locally')
      await saveContentTrust(accountId, trust)
      const rotated = await this.rotateLocked(accountId, trust, device)
      return {
        seq: relayed.seq,
        sessionsRevoked: relayed.sessionsRevoked,
        epoch: rotated.statement.epoch,
        unwrappedEscrows: rotated.unwrappedEscrows,
      }
    })
    this.work = next.catch(() => {})
    return next
  }

  /** Serialized access for recovery/escrow flows (content-recovery.ts). */
  exclusive<T>(fn: (ctx: KeyContext) => Promise<T>): Promise<T> {
    const next = this.work.then(async () => {
      const accountId = await this.accountId()
      return fn({
        accountId,
        api: this.api,
        passkeys: this.passkeys,
        vault: this.vault,
        local: () => this.local(accountId),
        refresh: (trust, device, extraKeys) => this.refresh(accountId, trust, device, extraKeys),
        publishNext: (trust, device, input) => this.publishNext(accountId, trust, device, input),
        escrowKeys: () => this.escrowKeys,
        joinTrusted: (trust, device) => this.joinTrusted(accountId, trust, device),
      })
    })
    this.work = next.catch(() => {})
    return next
  }

  private bound: string | undefined
  /** Bind this session to the device so revoking the device ends the session (best effort). */
  private async bindSessionOnce(accountId: string, device: StoredDevice) {
    if (typeof (this.api as Partial<Api>).contentKeys !== 'function') return
    if (this.bound === accountId) return
    this.bound = accountId
    try {
      const relay = keysApi(this.api)
      const { nonce } = await relay.sessionChallenge()
      const statement = { version: 1, accountId, deviceKeyId: device.deviceKeyId, nonce }
      const signature = await signStatement(
        { sign: (data) => signP256(device.privateKey, data) },
        SESSION_DEVICE_DOMAIN,
        statement,
      )
      await relay.bindSession({ statement, signature })
    } catch {
      this.bound = undefined
    }
  }
  /**
   * Can machine `machineId` read this account's encrypted content? Read-only (never prompts):
   * - `granted`: it is a daemon recipient of the verified directory head.
   * - `needs-grant`: its content identity verifies against this browser's enrollment pin, but no
   *   passkey-approved directory lists it yet. `currentKey(machineId)` grants it (one tap).
   * - `identity-pending`: vikod has not published a content identity (not connected yet, or old).
   * - `approve-elsewhere`: this browser never approved the machine, so it cannot vouch for its key.
   * - `locked`: this browser is not trusted for encrypted content.
   * A published identity that fails verification throws: it is never presented as grantable.
   */
  async machineAccess(machineId: string): Promise<MachineAccess> {
    let checked: Awaited<ReturnType<BrowserContentSession['check']>>
    try {
      checked = await this.check(STATUS_CHECK_MS)
    } catch (e) {
      if (e instanceof LocalTrustError) return 'locked'
      throw e
    }
    const { accountId, directory } = checked
    if (
      directory.checkpoint?.statement.recipients.some(
        (r) => r.id === machineId && r.type === 'daemon',
      )
    )
      return 'granted'
    let raw: unknown
    try {
      raw = await this.api.readContentIdentity(machineId)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'content_identity_pending') return 'identity-pending'
      throw e
    }
    if (!(await hasApprovedContentDaemon(accountId, machineId))) return 'approve-elsewhere'
    await verifiedDaemonContentKey(accountId, machineId, raw)
    return 'needs-grant'
  }
  /**
   * read-budget: the session + local trust + directory catch-up that precedes reads, shared by
   * every caller that starts while one runs or within its `maxAgeMs` of the last success.
   * Decrypting a page of entities used to cost one session request and one directory catch-up
   * per item; machine-access badges and lock probes each paid both again.
   */
  private checked:
    | {
        at?: number
        p: Promise<{
          accountId: string
          trust: TrustStore
          device: StoredDevice
          directory: Awaited<ReturnType<BrowserContentSession['refresh']>>
        }>
      }
    | undefined
  private check(maxAgeMs: number, force = false) {
    const c = this.checked
    if (!force && c && (c.at === undefined || this.now() - c.at < maxAgeMs)) return c.p
    const entry: NonNullable<typeof this.checked> = {
      p: (async () => {
        const accountId = await this.accountId()
        let local: { trust: TrustStore; device: StoredDevice }
        try {
          local = await this.local(accountId)
        } catch (e) {
          throw new LocalTrustError(e)
        }
        const directory = await this.refresh(accountId, local.trust, local.device)
        return { accountId, ...local, directory }
      })(),
    }
    this.checked = entry
    entry.p.then(
      () => {
        entry.at = this.now()
      },
      () => {
        if (this.checked === entry) this.checked = undefined
      },
    )
    return entry.p
  }
  /** The shared check for `accountId` (never another account's). */
  private async checkFor(accountId: string, maxAgeMs: number, force = false) {
    let c = await this.check(maxAgeMs, force)
    // A shared answer for another account (a switch just happened) never decides: ask again.
    if (c.accountId !== accountId && !force) c = await this.check(0, true)
    if (c.accountId !== accountId) throw new Error('Encrypted account changed')
    return c
  }
  /** Directory or trust changed here (grant, rotation, unlock, sign-out): the next read re-checks. */
  invalidate() {
    this.checked = undefined
  }
  /** Run `read`; on a missing epoch key re-check once at once (a rotation just happened). */
  private async withFreshEpochs<T>(accountId: string, read: () => Promise<T>): Promise<T> {
    await this.checkFor(accountId, READ_CHECK_MS)
    try {
      return await read()
    } catch (e) {
      if (!(e instanceof Error) || !/content locked/i.test(e.message)) throw e
      await this.checkFor(accountId, READ_CHECK_MS, true)
      return read()
    }
  }
  /**
   * True only when the relay has no signed directory and this browser never pinned one. A relay
   * that drops a head this browser pinned fails the refresh ('Encrypted directory rollback').
   */
  async unprovisioned(accountId: string) {
    return !(await this.checkFor(accountId, STATUS_CHECK_MS).catch(unwrapLocal)).directory
      .checkpoint
  }
  async activeKey() {
    const { accountId, trust, device } = await this.check(STATUS_CHECK_MS).catch(unwrapLocal)
    void this.bindSessionOnce(accountId, device)
    if (await this.couldJoin(accountId, device)) this.joinSoon(accountId)
    return { accountId, ...(await this.vault.currentKey(accountId, trust, device.deviceKeyId)) }
  }
  async readKey(accountId: string, epoch: number) {
    return this.withFreshEpochs(accountId, () => this.vault.epochKey(accountId, epoch)).catch(
      unwrapLocal,
    )
  }
  async transcriptBatch(
    accountId: string,
    runId: string,
    raw: readonly unknown[],
    minimumEpoch = 1,
  ) {
    return this.withFreshEpochs(accountId, () =>
      this.vault.transcriptBatch(accountId, runId, raw, minimumEpoch),
    ).catch(unwrapLocal)
  }
  async requestPayload(command: SignedCommand) {
    const sealed = EncryptedContent.parse(command.payload)
    const c = sealed.context
    if (
      c.kind !== 'command' ||
      c.seq !== 1 ||
      c.objectId !== command.envelope.id ||
      c.runId !== command.envelope.machineId
    )
      throw new Error('Encrypted command context mismatch')
    await this.readKey(c.accountId, c.epoch)
    return JSON.parse(new TextDecoder().decode(await this.vault.open(c, sealed.box))) as unknown
  }
  async outcome(command: SignedCommand, out: CommandOutcome): Promise<CommandOutcome> {
    // Relay progress has no private payload. Terminal daemon replies always carry ciphertext.
    if (out.result === undefined) {
      if (out.status === 'done' || out.status === 'failed')
        throw new Error('Encrypted command result missing')
      return { commandId: out.commandId, status: out.status }
    }
    const sent = EncryptedContent.parse(command.payload)
    const sealed = EncryptedContent.parse(
      typeof out.result === 'string' ? JSON.parse(out.result) : out.result,
    )
    const c = sealed.context
    if (
      out.commandId !== command.envelope.id ||
      c.accountId !== sent.context.accountId ||
      c.objectId !== command.envelope.id ||
      c.runId !== command.envelope.machineId ||
      c.kind !== 'command.result' ||
      c.seq !== 1
    )
      throw new Error('Encrypted command result mismatch')
    const opened = await this.withFreshEpochs(c.accountId, () =>
      this.vault.open(c, sealed.box),
    ).catch(unwrapLocal)
    const payload = JSON.parse(new TextDecoder().decode(opened)) as Record<string, unknown>
    if (payload.status !== out.status) throw new Error('Encrypted command status mismatch')
    return {
      commandId: out.commandId,
      status: out.status,
      ...(typeof payload.reason === 'string' ? { reason: payload.reason } : {}),
      ...(typeof payload.error === 'string' ? { error: payload.error } : {}),
      ...(payload.result !== undefined ? { result: payload.result } : {}),
    }
  }
}
